/**
 * Run-wide seeded RNG streams (SCOPE_STAGE2 Ziel 2): every shop/fracture/ghost draw is
 * derived from (masterSeed, purpose, counter) — integer-only, order-independent between
 * purposes, and fully serializable via the counters (Pause & Resume, A8).
 * NEVER Math.random() (CLAUDE.md rule 3).
 */

export type RngCounters = Record<string, number>

function fnv1a(str: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** stateless hash draw: same (seed, purpose, n) -> same uint32, murmur3-fmix finalized */
export function hashU32(masterSeed: number, purpose: string, n: number): number {
  let h = masterSeed >>> 0
  h = Math.imul(h ^ fnv1a(purpose), 0x9e3779b1) >>> 0
  h = Math.imul(h ^ ((n + 0x6d2b79f5) >>> 0), 0x85ebca6b) >>> 0
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca6b) >>> 0
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35) >>> 0
  h ^= h >>> 16
  return h >>> 0
}

/** draw next uint32 from a named stream, advancing its counter */
export function drawU32(masterSeed: number, counters: RngCounters, purpose: string): number {
  const n = counters[purpose] ?? 0
  counters[purpose] = n + 1
  return hashU32(masterSeed, purpose, n)
}

/** integer in [0, bound) from a named stream */
export function drawInt(masterSeed: number, counters: RngCounters, purpose: string, bound: number): number {
  if (bound <= 1) {
    // still consume a draw so call sites stay in lockstep regardless of bound
    drawU32(masterSeed, counters, purpose)
    return 0
  }
  return drawU32(masterSeed, counters, purpose) % bound
}

/** weighted pick: entries [key, weight>0][]; deterministic walk */
export function drawWeighted<T extends string>(
  masterSeed: number, counters: RngCounters, purpose: string,
  entries: Array<[T, number]>,
): T {
  let total = 0
  for (const [, w] of entries) total += w
  if (total <= 0 || entries.length === 0) throw new Error(`drawWeighted: no weight (${purpose})`)
  let r = drawU32(masterSeed, counters, purpose) % total
  for (const [key, w] of entries) {
    if (r < w) return key
    r -= w
  }
  return entries[entries.length - 1]![0]
}
